import { useEffect, useState } from 'react';
import { Table, Card, Button, Space, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { listMainlineProjects } from '@/api/requirements';
import { getProject } from '@/api/config';
import type { MainlineProject, Project } from '@/types';
import ProjectFormModal from '@/components/ProjectFormModal';

const MainlineRequirements = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<MainlineProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  const reload = () => {
    setLoading(true);
    listMainlineProjects()
      .then((res) => {
        if (res.code === 200) setData(res.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const handleCreate = () => {
    setEditingProject(null);
    setModalOpen(true);
  };

  const handleEdit = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const project = await getProject(String(id));
    setEditingProject(project);
    setModalOpen(true);
  };

  const columns: ColumnsType<MainlineProject> = [
    { title: '项目名称', dataIndex: 'project', key: 'project' },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', render: (v: string) => v ? new Date(v).toLocaleDateString('zh-CN') : '-' },
    { title: 'owner', dataIndex: 'owner', key: 'owner', render: (v: string | null) => v || '-' },
    { title: 'OS', dataIndex: 'os', key: 'os' },
    { title: '平台', dataIndex: 'platform', key: 'platform' },
    { title: '域控形态', dataIndex: 'adcuModel', key: 'adcuModel' },
    { title: 'MCU型号', dataIndex: 'mcuModel', key: 'mcuModel', render: (v) => v ?? '-' },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_, row) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Button size="small" icon={<EditOutlined />} onClick={(e) => handleEdit(row.id, e)} />
          <Tooltip title="主线项目不可删除">
            <Button size="small" danger icon={<DeleteOutlined />} disabled />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="主线需求"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          创建主线项目
        </Button>
      }
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
        onRow={(row) => ({
          onClick: () => navigate(`/mainline-project-requirements/${row.id}`),
          style: { cursor: 'pointer' },
        })}
      />
      <ProjectFormModal
        open={modalOpen}
        project={editingProject}
        defaultMainline
        onSuccess={() => {
          setModalOpen(false);
          reload();
        }}
        onCancel={() => setModalOpen(false)}
      />
    </Card>
  );
};

export default MainlineRequirements;
